import type { ReactNode } from "react";

const INLINE_PATTERN =
  /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)]+)\)|(https?:\/\/[^\s]+)|([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})|\*\*([^*]+)\*\*/gi;

const INTERNAL_VERSION_LINE = /^\*\*Versión:\*\*\s*productora-tc-v\d+\s*$/i;
const INTERNAL_VERSION_INLINE = /\*\*Versión:\*\*\s*productora-tc-v\d+\s*/gi;

/**
 * Drops internal productora contract version labels from display text.
 * Locked markdown bytes stay unchanged; this is presentation only.
 */
export function stripInternalProductoraVersionLabels(text: string): string {
  const withoutVersionLines = text
    .split("\n")
    .filter((line) => !INTERNAL_VERSION_LINE.test(line.trim()))
    .join("\n");
  return withoutVersionLines.replace(INTERNAL_VERSION_INLINE, "").replace(/[ \t]+\n/g, "\n").trim();
}

function renderInlineContent(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  INLINE_PATTERN.lastIndex = 0;

  for (;;) {
    match = INLINE_PATTERN.exec(text);
    if (!match) break;

    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const [fullMatch, markdownLabel, markdownHref, bareUrl, email, bold] = match;
    if (bold) {
      nodes.push(
        <strong key={`bold-${match.index}`} className="gruv-terms__strong">
          {bold}
        </strong>,
      );
    } else {
      const href = markdownHref ?? bareUrl ?? (email ? `mailto:${email}` : null);
      const label = markdownLabel ?? bareUrl ?? email ?? fullMatch;
      if (href) {
        nodes.push(
          <a
            key={`${href}-${match.index}`}
            href={href}
            className="gruv-terms__link"
            target={href.startsWith("http") ? "_blank" : undefined}
            rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
          >
            {label}
          </a>,
        );
      } else {
        nodes.push(fullMatch);
      }
    }

    lastIndex = match.index + fullMatch.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

function renderBrokenLines(text: string): ReactNode[] {
  const parts = text.split(/ {2}\n/);
  const nodes: ReactNode[] = [];
  parts.forEach((part, i) => {
    if (i > 0) {
      nodes.push(<br key={`br-${i}`} />);
    }
    nodes.push(...renderInlineContent(part));
  });
  return nodes;
}

export type LegalMarkdownTextProps = {
  text: string;
  className?: string;
  headingClassName?: string;
};

/**
 * Renders a legal markdown paragraph: ATX headings, bold, links, emails.
 * Internal productora-tc version labels are omitted from the DOM.
 */
export function LegalMarkdownText({ text, className, headingClassName }: LegalMarkdownTextProps) {
  const display = stripInternalProductoraVersionLabels(text);
  if (!display) {
    return null;
  }

  const heading = display.match(/^(#{1,6})\s+(.+)$/);
  if (heading) {
    const level = Math.min(heading[1].length, 6);
    const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
    return (
      <Tag className={headingClassName ?? className}>{renderBrokenLines(heading[2].trim())}</Tag>
    );
  }

  return <p className={className}>{renderBrokenLines(display)}</p>;
}
